import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { CCard, CCardBody, CSpinner } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilStar } from '@coreui/icons'
import { get } from '../../utils/apiClient'
import Avatar from '../../components/shared/Avatar'

const StarRow = ({ rating, size = 'sm' }) => {
  const { t } = useTranslation()
  return (
    <span
      aria-label={t('mentoring:testimonials.stars_aria', { count: rating })}
      title={t('mentoring:testimonials.stars_title', { rating })}
    >
      {[1, 2, 3, 4, 5].map((v) => (
        <CIcon
          key={v}
          icon={cilStar}
          size={size}
          style={{ color: v <= rating ? '#f5a524' : '#d1d5db' }}
        />
      ))}
    </span>
  )
}

const MentorTestimonials = ({ mentorId, maxVisible = 3, onAuthorClick }) => {
  const { t } = useTranslation()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    if (!mentorId) return
    let cancelled = false
    setLoading(true)
    get(`/mentoring/mentors/${mentorId}/testimonials`)
      .then((res) => {
        if (!cancelled) setData(res)
      })
      .catch(() => {
        if (!cancelled) setData({ rating: { avg: null, count: 0 }, testimonials: [] })
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [mentorId])

  if (loading) {
    return (
      <div className="text-center py-2">
        <CSpinner size="sm" />
      </div>
    )
  }

  if (!data || (data.rating.count === 0 && data.testimonials.length === 0)) {
    return null
  }

  const visible = showAll ? data.testimonials : data.testimonials.slice(0, maxVisible)

  return (
    <div className="mt-3">
      <h6 className="mb-2 d-flex align-items-center gap-2">
        <CIcon icon={cilStar} className="text-warning" />
        {t('mentoring:testimonials.title')}
        {data.rating.avg != null && (
          <span className="text-muted small">
            {t('mentoring:testimonials.rating_summary', {
              avg: data.rating.avg,
              count: data.rating.count,
            })}
          </span>
        )}
      </h6>

      {data.testimonials.length === 0 ? (
        <p className="text-muted small mb-0">{t('mentoring:testimonials.none_yet')}</p>
      ) : (
        <>
          {visible.map((item) => {
            const authorClickable = !!item.author && !!onAuthorClick
            const authorAction = authorClickable ? () => onAuthorClick(item.author) : undefined
            return (
              <CCard key={item.id} className="mb-2 shadow-sm">
                <CCardBody className="py-2 px-3">
                  <div className="d-flex align-items-start gap-2 mb-1">
                    {item.author && (
                      <span
                        onClick={authorAction}
                        style={{ cursor: authorClickable ? 'pointer' : 'default' }}
                        title={
                          authorClickable ? t('mentoring:testimonials.view_profile') : undefined
                        }
                      >
                        <Avatar user={item.author} size={28} />
                      </span>
                    )}
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2">
                        <strong
                          onClick={authorAction}
                          style={{
                            fontSize: '0.9rem',
                            cursor: authorClickable ? 'pointer' : 'default',
                          }}
                          title={
                            authorClickable ? t('mentoring:testimonials.view_profile') : undefined
                          }
                        >
                          {item.author
                            ? `${item.author.firstName} ${item.author.lastName}`
                            : t('mentoring:testimonials.deleted_member')}
                        </strong>
                        <StarRow rating={item.rating} />
                      </div>
                      <p className="mb-0 small fst-italic" style={{ lineHeight: 1.4 }}>
                        « {item.testimonial} »
                      </p>
                    </div>
                  </div>
                </CCardBody>
              </CCard>
            )
          })}
          {data.testimonials.length > maxVisible && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0"
              onClick={() => setShowAll((s) => !s)}
            >
              {showAll
                ? t('mentoring:testimonials.hide')
                : t('mentoring:testimonials.show_all', {
                    count: data.testimonials.length - maxVisible,
                  })}
            </button>
          )}
        </>
      )}
    </div>
  )
}

export default MentorTestimonials
